<template>
  <div :class="formGroupClass">
    <label class="control-label">{{ inputLabel }}</label>
    <div :class="['vue-tel-input', { disabled: disabled }]">
      <div
        v-click-outside="clickedOutside"
        aria-label="Country Code Selector"
        aria-haspopup="listbox"
        :aria-expanded="open"
        :class="['vti__dropdown', { open: open, disabled: disabled }]"
        @keydown="keyboardNav"
        @click="toggleDropdown"
        @keydown.space="toggleDropdown"
        @keydown.esc="reset"
        @keydown.tab="reset"
      >
        <span class="flag_selection" onselect="return false" onmousedown="return false">
          <img v-if="plate && plate.nationality && plate.nationality.iso2"
               :src="'img/icons/countries/' + plate.nationality.iso2.toUpperCase() + '.svg'" alt=""
               :class="['flag_img']">
          <slot name="arrow-icon" :open="open">
            <span class="vti__dropdown-arrow" v-if="isView === false">{{ open ? "▲" : "▼" }}</span>
          </slot>
        </span>
        <ul
          v-if="open"
          ref="list"
          class="vti__dropdown-list"
          :style="{ width: '390px', zIndex:2 }"
          :class="'below'"
        >
          <li
            v-for="(pb, index) in transportPlateMasks"
            :class="['vti__dropdown-item']"
            :key="pb.nationality.iso2"
            tabindex="-1"
            @click="setPlate(pb)"
            @mousemove="selectedIndex = index"
          >
            <div class="vti__flag-wrapper">
              <img v-if="pb && pb.nationality && pb.nationality.iso2"
                   :src="'img/icons/countries/' + pb.nationality.iso2.toUpperCase() + '.svg'" alt=""
                   :class="['flag_img']"
              >
              <span style="margin-left: 5px">
                <strong>
                {{ pb.nationality.full_name_en }}
                ({{ pb.nationality.full_name }})
                </strong>
                {{ pb.hint }}
              </span>
            </div>
          </li>
        </ul>
      </div>
      <input-facade
        v-model="inputValue"
        :id="inputId"
        :disabled="disabled"
        :placeholder="getPlateHint"
        :mask="getPlateMask"
        :tokens="inputFacadeTokens"
        class="text-uppercase input-facade"
        style="display: inline-block; width: 100%"
        masked
      />
    </div>
    <typeahead
      v-if="isTransportNumber"
      v-model="inputValue"
      :async-function="typeahead"
      :data="typeaheadData"
      :item-key="typeaheadItemKey"
      :limit="typeaheadLimit"
      :target="'#' + inputId"
      @input="selectTypeahead"
    />
    <error-help-block :has-errors="hasInputErrors" :current-error="actualErrorMessage"/>
    <slot name="after"/>
  </div>
</template>

<script>
import applicationFormMixin from '@/common/mixins/applicationFormMixin'
import applicationInputMixin from '@/common/mixins/applicationInputMixin'
import transportInputMixin from '../../../../mixins/application-form/transportInputMixin'
import ClickOutside from 'vue-click-outside'
import '@/common/assets/styles/transport-plate-style.scss'
import { InputFacade } from 'vue-input-facade'

export default {
  name: 'NumberPlateInput',

  components: {
    InputFacade
  },

  directives: {
    ClickOutside
  },

  mixins: [
    applicationFormMixin,
    applicationInputMixin,
    transportInputMixin
  ],

  props: {
    inputLabel: {
      default: 'Гос. регистрационный знак ТС'
    },

    isTransportNumber: {
      default: false
    },

    selectedPlate: {
      default: null
    },

    transportPlateMasks: {
      type: Array,
      default: () => []
    },

    visitor: {
      default: undefined
    }
  },

  data() {
    return {
      activeCountryCode: '',
      open: false,
      selectedIndex: null,
      typeToFindInput: '',
      typeToFindTimer: null,
      dropdownOpenDirection: 'below'
    };
  },

  computed: {
    getPlateHint() {
      return this.plate?.hint
    },

    getPlateMask() {
      if (this.plate && this.plate.mask) {
        return JSON.parse(this.plate.mask)
      }

      return null
    },

    inputFacadeTokens() {
      return {
        '#': { pattern: /\d/ },
        '*': { repeat: true },
        S: { pattern: /[a-zA-Z]/ },
        R: { pattern: /[А-Яа-я]/ },
        N: { pattern: /[IVXLCDM]/i },
        M: { pattern: /[0-9А-Яа-яa-zA-Z]/ }
      }
    },

    inputId() {
      return `form-visitor-transport-number-${this.visitorIndex}-${this.transportIndex}`
    },

    plate() {
      return this.selectedPlate ?? this.transportPlateMasks[0]
    },

    configuration() {
      return this.$store.getters.configuration
    }
  },

  watch: {
    open(isDropdownOpened) {
      if (isDropdownOpened) {
        this.setDropdownPosition();
        this.$emit('open');
      } else {
        this.$emit('close');
      }
    },

    plate(value) {
      this.$emit('change-plate', value)
    }
  },

  created() {
    if (Number(this.configuration.show_trailer_plate_input) === 0 && !this.isView) {
      this.transport.trailer_nationality_id = 182
    }
    this.$emit('change-plate', this.plate)
  },

  methods: {
    clickedOutside() {
      this.open = false;
    },

    keyboardNav(e) {
      if (e.keyCode === 40) {
        // down arrow
        e.preventDefault();
        this.open = true;
        if (this.selectedIndex === null) {
          this.selectedIndex = 0;
        } else {
          this.selectedIndex = Math.min(this.transportPlateMasks.length - 1, this.selectedIndex + 1);
        }
        const selEle = this.$refs.list.children[this.selectedIndex];
        selEle.focus();
        if (selEle.offsetTop + selEle.clientHeight > this.$refs.list.scrollTop + this.$refs.list.clientHeight) {
          this.$refs.list.scrollTop = selEle.offsetTop - this.$refs.list.clientHeight + selEle.clientHeight;
        }
      } else if (e.keyCode === 38) {
        // up arrow
        e.preventDefault();
        this.open = true;
        if (this.selectedIndex === null) {
          this.selectedIndex = this.transportPlateMasks.length - 1;
        } else {
          this.selectedIndex = Math.max(0, this.selectedIndex - 1);
        }
        const selEle = this.$refs.list.children[this.selectedIndex];
        selEle.focus();
        if (selEle.offsetTop < this.$refs.list.scrollTop) {
          this.$refs.list.scrollTop = selEle.offsetTop;
        }
      } else if (e.keyCode === 13) {
        // enter key
        if (this.selectedIndex !== null) {
          this.setPlate(this.transportPlateMasks[this.selectedIndex]);
        }
        this.open = !this.open;
      } else {
        // typing a country's name
        this.typeToFindInput += e.key;
        clearTimeout(this.typeToFindTimer);
        this.typeToFindTimer = setTimeout(() => {
          this.typeToFindInput = '';
        }, 700);
        // don't include preferred countries, so we jump to the right place in the alphabet
        const typedCountryI = this.transportPlateMasks
          .slice(this.preferredCountries.length)
          .findIndex((c) => c.name.toLowerCase().startsWith(this.typeToFindInput));
        if (typedCountryI >= 0) {
          this.selectedIndex = this.preferredCountries.length + typedCountryI;
          const selEle = this.$refs.list.children[this.selectedIndex];
          const needToScrollTop = selEle.offsetTop < this.$refs.list.scrollTop;
          const needToScrollBottom = selEle.offsetTop + selEle.clientHeight > this.$refs.list.scrollTop + this.$refs.list.clientHeight;
          if (needToScrollTop || needToScrollBottom) {
            this.$refs.list.scrollTop = selEle.offsetTop - this.$refs.list.clientHeight / 2;
          }
        }
      }
    },

    reset() {
      this.selectedIndex = this.transportPlateMasks.map((c) => c.nationality.iso2).indexOf(this.activeCountryCode);
      this.open = false;
    },

    setDropdownPosition() {
      const spaceBelow = window.innerHeight - this.$el.getBoundingClientRect().bottom;
      const hasEnoughSpaceBelow = spaceBelow > 200;
      if (hasEnoughSpaceBelow) {
        this.dropdownOpenDirection = 'below';
      } else {
        this.dropdownOpenDirection = 'above';
      }
    },

    setPlate(countryPlate) {
      this.$emit('change-plate', countryPlate)
    },

    toggleDropdown(e) {
      if (this.disabled || e?.path?.[0]?.type === 'text') {
        return;
      }
      this.open = !this.open;
    }
  }
}
</script>
